import api from './api'
import type {
  EAProject,
  EAProjectCreate,
  EAProjectUpdate,
  EAProjectListItem,
  EAPipelineTask,
  EAPipelineTaskListItem,
  EAArtifact,
  EAArtifactListItem,
  EAVaultDocument,
  EAVaultDocumentCreate,
  EAVaultDocumentUpdate,
  EAVaultDocumentListItem,
  EAVaultFreshnessReport,
  EAVaultSearchResult,
  EAHandoverLogEntry,
  EAQCFinding,
  EAQCFindingListItem,
} from '../types/ea'

// EA Projects
export const getEAProjects = (params?: { status?: string; client_slug?: string; vendor?: string; phase?: string; limit?: number; offset?: number }) =>
  api.get<EAProjectListItem[]>('/api/ea/projects', { params })

export const getEAProject = (projectId: string) =>
  api.get<EAProject>(`/api/ea/projects/${projectId}`)

export const createEAProject = (data: EAProjectCreate) =>
  api.post<EAProject>('/api/ea/projects', data)

export const updateEAProject = (projectId: string, data: EAProjectUpdate) =>
  api.put<EAProject>(`/api/ea/projects/${projectId}`, data)

export const deleteEAProject = (projectId: string) =>
  api.delete(`/api/ea/projects/${projectId}`)

// EA Artifacts
export const getEAArtifacts = (projectId: string, params?: { phase?: string; vendor?: string; artifact_type?: string; limit?: number; offset?: number }) =>
  api.get<EAArtifactListItem[]>(`/api/ea/artifacts/projects/${projectId}/artifacts`, { params })

export const getEAArtifact = (artifactId: string) =>
  api.get<EAArtifact>(`/api/ea/artifacts/${artifactId}`)

export const getEADeliverables = (projectId: string) =>
  api.get<EAArtifactListItem[]>(`/api/ea/artifacts/projects/${projectId}/deliverables`)

export const promoteEAArtifact = (artifactId: string) =>
  api.post<EAArtifact>(`/api/ea/artifacts/${artifactId}/promote`)

// EA Artifact Preview/Download/Upload
export const previewEAArtifact = (artifactId: string) =>
  api.get<{ content: string; format: string }>(`/api/ea/artifacts/${artifactId}/preview`)

export const downloadEAArtifact = (artifactId: string) =>
  api.get(`/api/ea/artifacts/${artifactId}/download`, { responseType: 'blob' })

export const uploadEAArtifact = (projectId: string, formData: FormData) =>
  api.post(`/api/ea/artifacts/projects/${projectId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

// EA Vault
export const getVaultDocuments = (params?: { vendor?: string; category?: string; stale_only?: boolean; limit?: number; offset?: number }) =>
  api.get<EAVaultDocumentListItem[]>('/api/ea/vault', { params })

export const getVaultDocument = (docId: string) =>
  api.get<EAVaultDocument>(`/api/ea/vault/${docId}`)

export const createVaultDocument = (data: EAVaultDocumentCreate) =>
  api.post<EAVaultDocument>('/api/ea/vault', data)

export const updateVaultDocument = (docId: string, data: EAVaultDocumentUpdate) =>
  api.put<EAVaultDocument>(`/api/ea/vault/${docId}`, data)

export const deleteVaultDocument = (docId: string) =>
  api.delete(`/api/ea/vault/${docId}`)

export const getVaultFreshness = (vendor: string) =>
  api.get<EAVaultFreshnessReport>(`/api/ea/vault/${vendor}/freshness`)

export const searchVault = (query: string, vendor?: string, topK?: number) =>
  api.post<EAVaultSearchResult[]>('/api/ea/vault/search', { query, vendor, top_k: topK })

// EA Tasks
export const submitEATask = (projectId: string, data: { task_description: string }) =>
  api.post<EAPipelineTask>(`/api/ea/tasks/projects/${projectId}/submit`, data)

export const getEATasks = (projectId: string, params?: { status?: string; task_type?: string; limit?: number; offset?: number }) =>
  api.get<EAPipelineTaskListItem[]>(`/api/ea/tasks/projects/${projectId}/tasks`, { params })

export const getEATask = (taskId: string) =>
  api.get<EAPipelineTask>(`/api/ea/tasks/${taskId}`)

export const cancelEATask = (taskId: string) =>
  api.post(`/api/ea/tasks/${taskId}/cancel`)

export const retryEATask = (taskId: string) =>
  api.post<EAPipelineTask>(`/api/ea/tasks/${taskId}/retry`)

// EA Handover
export const getGlobalHandoverLog = (params?: { limit?: number; offset?: number }) =>
  api.get<EAHandoverLogEntry[]>('/api/ea/handover/global', { params })

export const getProjectHandoverLog = (projectId: string, params?: { limit?: number; offset?: number }) =>
  api.get<EAHandoverLogEntry[]>(`/api/ea/handover/projects/${projectId}`, { params })

// EA QC
export const getQCFindings = (projectId: string, params?: { severity?: string; status?: string; vendor?: string }) =>
  api.get<EAQCFindingListItem[]>(`/api/ea/qc/projects/${projectId}/findings`, { params })

export const getQCFinding = (findingId: string) =>
  api.get<EAQCFinding>(`/api/ea/qc/findings/${findingId}`)

export const updateQCFinding = (findingId: string, data: { status?: string; resolution_notes?: string }) =>
  api.put<EAQCFinding>(`/api/ea/qc/findings/${findingId}`, data)

export const approveQC = (projectId: string) =>
  api.post(`/api/ea/qc/projects/${projectId}/approve`)

export const rejectQC = (projectId: string) =>
  api.post(`/api/ea/qc/projects/${projectId}/reject`)

export const getQCReport = (projectId: string) =>
  api.get(`/api/ea/qc/projects/${projectId}/report`)
